// form_styling.js
// Import our custom css
const htmlElementCSS = require("../../styles/html_element.css").default; // Correct import

/**
 * Returns a safe external CSS URL for the Data Hub stylesheet when one is provided.
 * Uses the browser URL parser so cache-busted stylesheet URLs are accepted while
 * malformed values, non-HTTPS absolute URLs, and non-CSS paths are rejected before
 * the value is assigned to a DOM link element.
 *
 * @param {string|null} css_url - Candidate stylesheet URL.
 * @returns {string|null} Normalized stylesheet URL, or null when invalid.
 */
function normalize_external_data_hub_css_url(css_url) {
  if (!css_url || typeof css_url !== "string") {
    return null;
  }

  const trimmed_css_url = css_url.trim();
  if (!trimmed_css_url) {
    return null;
  }

  const is_root_relative_url = trimmed_css_url.startsWith("/") && !trimmed_css_url.startsWith("//");

  try {
    const base_url = is_root_relative_url
      ? "https://data-hub.local/"
      : typeof document !== "undefined" && document.baseURI
      ? document.baseURI
      : "https://data-hub.local/";
    const parsed_css_url = new URL(trimmed_css_url, base_url);

    if (parsed_css_url.protocol !== "https:") {
      return null;
    }

    if (!parsed_css_url.pathname.toLowerCase().endsWith(".css")) {
      return null;
    }

    if (is_root_relative_url) {
      return `${parsed_css_url.pathname}${parsed_css_url.search}${parsed_css_url.hash}`;
    }

    return parsed_css_url.href;
  } catch (error) {
    return null;
  }
}

/**
 * Resolves where a stylesheet link should be inserted.
 *
 * @param {HTMLElement|ShadowRoot|Document|DocumentFragment} target - Document, element, or shadow root receiving CSS.
 * @returns {HTMLElement|ShadowRoot|DocumentFragment|null} Resolved insertion target.
 */
function resolve_css_injection_target(target) {
  if (!target) {
    return null;
  }

  if (target.nodeType === 9) {
    return target.head || target.documentElement || null;
  }

  return target;
}

/**
 * Removes previously injected Data Hub stylesheet nodes so each target has one canonical stylesheet.
 *
 * @param {HTMLElement|ShadowRoot|DocumentFragment} target - Element or shadow root receiving CSS.
 */
function remove_existing_data_hub_css_nodes(target) {
  target.querySelectorAll("link[data-hub-css]").forEach((existing_data_hub_css_node) => {
    existing_data_hub_css_node.remove();
  });
}

/**
 * Injects Data Hub CSS into a document, element, or shadow root.
 *
 * Data Hub CSS is expected to be a generated Tailwind/custom stylesheet hosted by the
 * caller, CDN, or local action app. The JavaScript bundle no longer embeds generic
 * Tailwind utility CSS, so no URL means no stylesheet node is injected.
 *
 * @param {HTMLElement|ShadowRoot|Document|DocumentFragment} target - Document, element, or shadow root receiving CSS.
 * @param {Object|string} options - Optional settings object, or a CSS URL string.
 * @param {string|null} options.css_url - Optional external Tailwind stylesheet URL.
 * @param {string|null} options.data_hub_css_url - Optional external Data Hub stylesheet URL.
 * @param {Function|null} options.error_function - Optional error handler.
 * @returns {HTMLLinkElement|null} Injected stylesheet node, or null on error.
 */
function inject_data_hub_css_link(target, options = {}) {
  const resolved_target = resolve_css_injection_target(target);
  if (!resolved_target) {
    window?.data_hub?.error_function?.("No container provided to inject Data Hub CSS", null);
    return null;
  }

  const css_url = typeof options === "string" ? options : (options.data_hub_css_url || options.css_url);
  const error_function = typeof options === "object" ? options.error_function : null;
  const normalized_css_url = normalize_external_data_hub_css_url(css_url);

  if (css_url && !normalized_css_url) {
    error_function?.("Invalid Data Hub CSS URL provided", null);
    return null;
  }

  if (!normalized_css_url) {
    return null;
  }

  const existing_data_hub_css_link = Array.from(resolved_target.querySelectorAll("link[data-hub-css], link[rel='stylesheet']")).find((link_element) => {
    return link_element.getAttribute("href") === normalized_css_url;
  });

  if (existing_data_hub_css_link) {
    existing_data_hub_css_link.setAttribute("data-hub-css", "");
    existing_data_hub_css_link.setAttribute("data-hub-css-url", normalized_css_url);
    return existing_data_hub_css_link;
  }

  remove_existing_data_hub_css_nodes(resolved_target);

  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = normalized_css_url;
  link.setAttribute("data-hub-css", "");
  link.setAttribute("data-hub-css-url", normalized_css_url);
  resolved_target.appendChild(link);
  return link;
}

/**
 * Backward-compatible alias for older callers that still ask DataHub to inject Tailwind.
 * This now injects only the external Data Hub stylesheet URL and never embeds generated
 * Tailwind CSS inside the JavaScript bundle.
 *
 * @param {HTMLElement|ShadowRoot|Document|DocumentFragment} target - Document, element, or shadow root receiving CSS.
 * @param {Object|string} options - Optional settings object, or a CSS URL string.
 * @returns {HTMLLinkElement|null} Injected stylesheet node, or null on error.
 */
function inject_tailwind_css(target, options = {}) {
  return inject_data_hub_css_link(target, options);
}

function inject_html_element_css(container) {
  if (!container) {
    window?.data_hub?.error_function?.("No container provided to inject HTML element CSS", null);
    return;
  }
  if (!container.querySelector("style[data-html-element]")) {
    const style = document.createElement("style");
    style.textContent = htmlElementCSS;
    style.setAttribute("data-html-element", "");
    style.type = "text/css";
    container.appendChild(style);
  }
}

function get_tailwind_size_class(size) {
  switch (size) {
    case "1/4":
      return "w-1/4";
    case "1/2":
      return "w-1/2";
    case "1/3":
      return "w-1/3";
    case "2/3":
      return "w-2/3";
    case "3/4":
      return "w-3/4";
    case "full":
      return "w-full";
    default:
      return "w-full"; // default if no valid size is found
  }
}

function get_field_width_fraction(size = "w-full") {
  // size is like 'w-1/2', 'w-full', etc.
  if (size === "w-full") {
    return 1;
  }
  const match = size.match(/^w-(\d+)\/(\d+)$/);
  if (match) {
    const numerator = parseInt(match[1], 10);
    const denominator = parseInt(match[2], 10);
    if (denominator !== 0) {
      return numerator / denominator;
    }
  }
  return 1; // Default to full width if parsing fails
}

function update_original_height(selected_element) {
  const container = selected_element.querySelector(".data-hub-container");
  if (container) {
    return container.offsetHeight;
  }
}

function run_theme_json_to_base64(theme) {
  if (!theme) {
    window?.data_hub?.error_function?.("No theme object provided to convert to base64", null);
    return null;
  }
  return btoa(JSON.stringify(theme));
}

function parse_theme_object_string(context) {
  try {
    const themeString = atob(context.dataset.hubTheme);
    context.passed_theme = JSON.parse(themeString);
  } catch (e) {
    window?.data_hub?.error_function?.("Error parsing data-hub-theme: " + e, null);
    context.passed_theme = null;
  }
}

function update_brand_colors(context) {
  const colorTypes = [
    "primary_brand_color",
    "contrast_brand_color",
    "highlight_brand_color",
    "grey_brand_color",
  ];
  colorTypes.forEach((colorType) => {
    if (!context[colorType] && context.theme[colorType]?.[600]) {
      context[colorType] = context.theme[colorType][600];
    }
    // i think this is done further down in processing hsl etc. but if do this need to change to make sure [] is {} object not "string"
    // } else if (context[colorType] && !context.theme[colorType]?.[600]) {
    //     // if it's
    //     context.theme[colorType][600] = context[colorType];
    // }
  });
}

function assign_theming_variables(context) {
  if (context.dataset.hubTheme) {
    parse_theme_object_string(context);
  } else {
    // Check for specific attributes for colors
    context.primary_brand_color = context.getAttribute(
      "data-hub-primary-brand-color"
    );
    context.contrast_brand_color = context.getAttribute(
      "data-hub-contrast-brand-color"
    );
    context.highlight_brand_color = context.getAttribute(
      "data-hub-highlight-brand-color"
    );
    context.grey_brand_color = context.getAttribute(
      "data-hub-grey-brand-color"
    );

    // Update default theme colors if individual attributes are present
    if (context.primary_brand_color) {
      context.default_theme.primary_brand_color = context.primary_brand_color;
    }
    if (context.contrast_brand_color) {
      context.default_theme.contrast_brand_color = context.contrast_brand_color;
    }
    if (context.highlight_brand_color) {
      context.default_theme.highlight_brand_color =
        context.highlight_brand_color;
    }
    if (context.grey_brand_color) {
      context.default_theme.grey_brand_color = context.grey_brand_color;
    }

    // Fallback: if no explicit attributes were set, adopt theme_json from organization_metadata.
    // By the time the web component reaches this point, data_hub_class has finished loading
    // organization_metadata (renderForm is gated on the init callback), so this is synchronous.
    const has_individual_color_attr =
      context.primary_brand_color ||
      context.contrast_brand_color ||
      context.highlight_brand_color ||
      context.grey_brand_color;
    if (!has_individual_color_attr) {
      const metadata_theme =
        context.data_hub_instance?.organization_metadata?.theme_json ||
        (typeof window !== "undefined" && window.data_hub_organization_metadata?.theme_json) ||
        null;
      if (metadata_theme && typeof metadata_theme === "object") {
        context.passed_theme = metadata_theme;
      }
    }
  }

  context.theme = context.passed_theme
    ? { ...context.default_theme, ...context.passed_theme }
    : context.default_theme;
  update_brand_colors(context);
}

function addBorderClassesToContainer(
  form_border,
  data_hub_form_element_container,
  error_function = window?.data_hub?.error_function
) {
  let borderClasses = [];
  let hasAtLeastOneBorderClass = false;
  const defaultBorderClasses = [
    "shadow-md",
    "bg-white",
    "rounded-md",
    "border",
    "border-grey-brand-color-400",
  ];

  if (form_border) {
    if (typeof form_border === "string") {
      if (form_border.toUpperCase() === "TRUE") {
        borderClasses = defaultBorderClasses;
        hasAtLeastOneBorderClass = true;
      } else if (form_border.toUpperCase() !== "FALSE") {
        // Handle both comma-separated and space-separated class lists
        let splitBorderClasses;

        if (form_border.includes(",")) {
          // If commas are present, split by commas (original behavior)
          splitBorderClasses = form_border.split(",").map((s) => s.trim());
        } else {
          // If no commas, assume space-separated format
          splitBorderClasses = form_border
            .split(/\s+/)
            .filter((s) => s.length > 0);
        }

        const allowedClassPrefixes = [
          "border",
          "rounded",
          "shadow",
          "bg",
          "m-",
          "p-",
          "mt-",
          "mb-",
          "ml-",
          "mr-",
          "pt-",
          "pb-",
          "pl-",
          "pr-",
          "px-", 
          "py-",
        ];

        borderClasses = splitBorderClasses.filter((cls) =>
          allowedClassPrefixes.some((prefix) => cls.startsWith(prefix))
        );
        hasAtLeastOneBorderClass = borderClasses.length > 0;
      }
    } else {
      error_function?.("form_border must be a string");
      borderClasses = defaultBorderClasses;
    }
  }

  if (hasAtLeastOneBorderClass) {
    // Add classes individually to avoid DOMTokenList error
    borderClasses.forEach((cls) => {
      if (cls && cls.trim()) {
        data_hub_form_element_container.classList.add(cls.trim());
      }
    });
  }
}

function force_tailwind_border_classes() {
  // Force Tailwind border classes to be generated
  const tailwindBorderColors = [
    "purple",
    "emerald",
    "rose",
    "amber",
    "sky",
    "indigo",
    "fuchsia",
    "lime",
    "cyan",
    "violet",
    "orange",
    "green",
    "red",
    "yellow",
    "blue",
    "teal",
    "gray",
  ];

  let borderClassCombinations = [];
  tailwindBorderColors.forEach((color) => {
    borderClassCombinations.push(`border-${color}`);
  });

  const tempDiv = document.createElement("div");
  tempDiv.style.display = "none";
  document.body.appendChild(tempDiv);
  borderClassCombinations.forEach((borderClass) => {
    tempDiv.classList.add(borderClass);
  });
  document.body.removeChild(tempDiv);

  return borderClassCombinations;
}

// Export
export {
  force_tailwind_border_classes,
  run_theme_json_to_base64,
  get_field_width_fraction,
  inject_data_hub_css_link,
  inject_tailwind_css,
  inject_html_element_css,
  get_tailwind_size_class,
  parse_theme_object_string,
  assign_theming_variables,
  update_original_height,
  addBorderClassesToContainer,
};
